import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { Fragment, useEffect, useState } from "react";
import { safeDiv } from "../Util";

type SizeProps = {
  size: number;
  titleSizes: string;
};

type FontProps = {
  size: string;
  weight: string;
  justify: string;
  index: number;
  startColorIndex: number;
};

type Props = {
  data: { name: string; value: number; max: number; count?: number }[];
  titleJustify: string;
  titleFontSize: string;
  titleFontWeight: string;
  valueFontSize: string;
  valueFontWeight: string;
  maxFontSize: string;
  maxFontWeight: string;
  unit: string;
  height: number;
  startColorIndex: number;
};

const backgroundColors = [
  "#4f46e5",
  "#0ea5e9",
  "#10b981",
  "#8b5cf6",
  "#f59e0b",
  "#14b8a6",
  "#f43f5e",
  "#84cc16",
  "#6366f1",
  "#ec4899",
  "#a3e2f1",
  "#ffff00",
  "#ff00ff",
  "#00ffff",
  "#0000ff",
  "#ff0000",
];
const remainBackgroundColor = "#e7e7e7";
const labelColor = "#fff";

function ProgressChartOnly({
  data,
  titleJustify,
  titleFontSize,
  titleFontWeight,
  valueFontSize,
  valueFontWeight,
  maxFontSize,
  maxFontWeight,
  unit,
  height,
  startColorIndex,
  isShowDetail = true,
}: {
  data: { name: string; value: number; count?: number }[];
  titleJustify: string;
  titleFontSize: string;
  titleFontWeight: string;
  valueFontSize: string;
  valueFontWeight: string;
  maxFontSize: string;
  maxFontWeight: string;
  unit: string;
  height: number;
  startColorIndex: number;
  isShowDetail?: boolean;
}) {
  const [chartData, setChartData] = useState<any>();
  const [maxValue, setMaxValue] = useState<number>(0);
  const [titleSizes, setTitleSizes] = useState<string>("");

  useEffect(() => {
    let chartData: any = {};

    chartData["name"] = "bardata";

    let totalValue = data.reduce((acc, cur) => acc + cur.value, 0);

    const titleRatios = [];

    for (let i = 0; i < data.length; i++) {
      chartData[`data${i}`] = safeDiv(data[i].value, totalValue) * 100;
      chartData[`chartData${i}`] = safeDiv(data[i].value, totalValue) * 100;

      titleRatios.push(safeDiv(data[i].value, totalValue) * 100);
    }

    setTitleSizes(titleRatios.join("fr ") + "fr");

    setChartData(chartData);
    setMaxValue(totalValue);
  }, [data]);

  const getColorIndex = (index: number): number => {
    return (index + startColorIndex) % backgroundColors.length;
  };

  return (
    <Container>
      <ResponsiveContainer height={height} width="100%">
        <BarChart
          layout="vertical"
          data={[chartData]}
          margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
          stackOffset="expand"
        >
          <XAxis type="number" hide={true} range={[0, maxValue]} />
          <YAxis
            type="category"
            dataKey="name"
            stroke="#fff"
            fontSize="12"
            hide
          />
          {data.map((item: any, index: number) => {
            return (
              <Bar
                key={index}
                className="bar"
                dataKey={`data${index}`}
                fill={backgroundColors[getColorIndex(index)]}
                stroke="#000"
                strokeWidth={0.4}
                stackId="a"
              >
                <LabelList
                  dataKey={`data${index}`}
                  style={{
                    fill: labelColor,
                    stroke: labelColor,
                    textAlign: "left",
                    textAnchor: "middle",
                    fontSize: valueFontSize,
                    fontWeight: valueFontWeight,
                  }}
                  // position={"insideLeft"}
                  formatter={(v: number): string => `${v.toFixed(1)}`}
                />
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
      <ChartTitles size={data.length} titleSizes={titleSizes}>
        {data.map((item, index) => (
          <ChartTitle
            size={titleFontSize}
            weight={titleFontWeight}
            justify={titleJustify}
            index={index}
            startColorIndex={startColorIndex}
            key={index}
          >
            <div>
              <span>{item.name}</span> <b>{item.value.toLocaleString()}</b>
              {unit}
            </div>
          </ChartTitle>
        ))}
      </ChartTitles>
    </Container>
  );
}

const Container = styled.div`
  padding-bottom: 20px;
`;

const ChartTitles = styled.div`
  display: grid;
  justify-content: start;
  grid-template-columns: ${(props: SizeProps) => `${props.titleSizes}`};
  padding-left: 2px;
  padding-bottom: 20px;

  & > div:nth-child(2),
  & > div:nth-child(3),
  & > div:nth-child(4),
  & > div:nth-child(5),
  & > div:nth-child(6) {
    //border-left: 1px solid #ccc;
  }
`;

const ChartTitle = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${(props: FontProps) => props.justify};
  text-align: center;
  font-weight: ${(props: FontProps) => props.weight};
  padding: 4px 0 0 0;
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  white-space: nowrap;
  text-align: left;

  div {
    padding-right: 10px;
    font-size: ${(props: FontProps) => props.size};
  }
  span {
    background-color: ${(props: FontProps) =>
      backgroundColors[
        (props.index + props.startColorIndex) % backgroundColors.length
      ]};
    color: #fff;
    border-radius: 5px;
    padding: 0 5px;
  }
`;

export default ProgressChartOnly;
